/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { sanitizeForSearch } from "@/lib/process-package";

export type ParsedSearchQuery = {
  hasQuotes: boolean;
  looseTerms: string[];
  exactPhrases: string[];
};

function containsPhraseWithBoundaries(
  sanitizedHaystack: string,
  sanitizedPhrase: string,
): boolean {
  if (!sanitizedPhrase) return true;
  if (!sanitizedHaystack) return false;

  let fromIndex = 0;
  while (true) {
    const index = sanitizedHaystack.indexOf(sanitizedPhrase, fromIndex);
    if (index === -1) return false;

    const beforeOk = index === 0 || sanitizedHaystack[index - 1] === " ";
    const afterIndex = index + sanitizedPhrase.length;
    const afterOk =
      afterIndex === sanitizedHaystack.length ||
      sanitizedHaystack[afterIndex] === " ";

    if (beforeOk && afterOk) return true;
    fromIndex = index + 1;
  }
}

export function parseSearchQuery(raw?: string): ParsedSearchQuery {
  const input = raw ?? "";
  const hasQuotes = input.includes('"');

  const phrasePattern = /"([^"]+)"/g;
  const exactPhrasesRaw: string[] = [];
  const cleaned = input.replace(phrasePattern, (_match, phrase) => {
    exactPhrasesRaw.push(String(phrase));
    return " ";
  });

  const exactPhrases = exactPhrasesRaw
    .map((phrase) => sanitizeForSearch(phrase))
    .filter(Boolean);

  const looseTerms = sanitizeForSearch(cleaned).split(/\s+/).filter(Boolean);

  return { hasQuotes, looseTerms, exactPhrases };
}

function matchesExactPhrases(
  sanitizedHaystack: string,
  exactPhrases: string[],
): boolean {
  for (const phrase of exactPhrases) {
    if (!containsPhraseWithBoundaries(sanitizedHaystack, phrase)) return false;
  }
  return true;
}

export function matchesSearchQuery(
  sanitizedHaystack: string,
  parsed: ParsedSearchQuery,
): boolean {
  const { looseTerms, exactPhrases } = parsed;

  if (looseTerms.length === 0 && exactPhrases.length === 0) return false;

  if (!matchesExactPhrases(sanitizedHaystack, exactPhrases)) return false;

  for (const term of looseTerms) {
    if (!sanitizedHaystack.includes(term)) return false;
  }

  return true;
}

export function getSingleQuotedPhrase(raw?: string): string | null {
  if (!raw) return null;
  const match = raw.trim().match(/^"([^"]+)"$/);
  return match?.[1] ?? null;
}
